import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';
import { Award, Image, User, Check, X, RefreshCw } from 'lucide-react';

const API_URL = "";

export const AdminArtistOfMonth = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentArtist, setCurrentArtist] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [customTitle, setCustomTitle] = useState('');
  const [customBio, setCustomBio] = useState('');
  const [isManualSelection, setIsManualSelection] = useState(false);

  const isAdmin = user?.access && ['Administrator', 'Webmaster', 'Admin'].includes(user.access);

  useEffect(() => {
    if (token && isAdmin) {
      fetchData();
    }
  }, [token, isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [currentRes, candidatesRes] = await Promise.all([
        fetch(`${API_URL}/api/members/artist-of-month`),
        fetch(`${API_URL}/api/members/artist-of-month/candidates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (currentRes.ok) {
        const data = await currentRes.json();
        setCurrentArtist(data.artist);
        setIsManualSelection(data.is_manual_selection);
        setCustomTitle(data.custom_title || '');
      }

      if (candidatesRes.ok) {
        const data = await candidatesRes.json();
        setCandidates(data.candidates || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectArtist = async () => {
    if (!selectedMember) {
      toast.error('Please select a member');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('member_id', selectedMember.id);
      if (customTitle) params.append('custom_title', customTitle);
      if (customBio) params.append('custom_bio', customBio);

      const response = await fetch(`${API_URL}/api/members/artist-of-month?${params.toString()}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update');
      }

      toast.success(`${selectedMember.first_name} ${selectedMember.last_name} is now Artist of the Month!`);
      setSelectedMember(null);
      setCustomTitle('');
      setCustomBio('');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to set Artist of the Month');
    } finally {
      setSaving(false);
    }
  };

  const handleClearSelection = async () => {
    if (!window.confirm('Clear manual selection? The homepage will auto-select the member with the most artwork.')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/members/artist-of-month`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to clear selection');
      }

      toast.success('Selection cleared. Homepage will auto-select based on artwork count.');
      fetchData();
    } catch (error) {
      console.error('Error clearing selection:', error);
      toast.error(error.message || 'Failed to clear selection');
    } finally {
      setSaving(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  if (loading) {
    return (
      <AdminLayout title="Artist of the Month">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Artist of the Month">
      <div className="space-y-8">
        {/* Current Selection */}
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="text-cafa-red" size={24} />
              Current Artist of the Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentArtist ? (
              <div className="flex items-start gap-6">
                {/* Artist Photo */}
                <div className="flex-shrink-0">
                  {currentArtist.photo_url ? (
                    <img
                      src={getImageUrl(currentArtist.photo_url)}
                      alt={`${currentArtist.first_name} ${currentArtist.last_name}`}
                      className="w-24 h-24 rounded-full object-cover ring-4 ring-cafa-red-100"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-cafa-red-100 flex items-center justify-center ring-4 ring-cafa-red-50">
                      <User size={40} className="text-cafa-red" />
                    </div>
                  )}
                </div>

                {/* Artist Info */}
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-stone-900">
                    {currentArtist.first_name} {currentArtist.last_name}
                  </h3>
                  {(currentArtist.city || currentArtist.state) && (
                    <p className="text-stone-500">
                      {[currentArtist.city, currentArtist.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-stone-600">
                    <Image size={16} />
                    <span>{currentArtist.gallery_count} artworks</span>
                  </div>
                  
                  {/* Selection Type Badge */}
                  <div className="mt-3">
                    {isManualSelection ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full">
                        <Check size={12} />
                        Manually Selected
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        <RefreshCw size={12} />
                        Auto-Selected (Most Artwork)
                      </span>
                    )}
                  </div>
                </div>

                {/* Gallery Preview */}
                {currentArtist.gallery_preview && currentArtist.gallery_preview.length > 0 && (
                  <div className="flex gap-2">
                    {currentArtist.gallery_preview.slice(0, 3).map((img, idx) => (
                      <div key={img.id || idx} className="w-20 h-20 rounded overflow-hidden bg-stone-100">
                        <img
                          src={getImageUrl(img.url)}
                          alt={img.title || 'Artwork'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Clear Button */}
                {isManualSelection && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearSelection}
                    disabled={saving}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <X size={14} className="mr-1" />
                    Clear Selection
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <Award size={48} className="mx-auto mb-4 text-stone-300" />
                <p>No Artist of the Month selected</p>
                <p className="text-sm">Select a member below to feature them on the homepage</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selection Form */}
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle>Select New Artist of the Month</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Candidates Grid */}
            <div>
              <Label className="text-base font-medium mb-3 block">Choose a Member</Label>
              <p className="text-sm text-stone-500 mb-4">
                Members with public artwork are shown below, sorted by artwork count.
              </p>
              
              {candidates.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {candidates.map((candidate) => (
                    <button
                      key={candidate.id}
                      onClick={() => setSelectedMember(candidate)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedMember?.id === candidate.id
                          ? 'border-cafa-red bg-cafa-red-50'
                          : 'border-stone-200 hover:border-stone-300 bg-white'
                      }`}
                      data-testid={`candidate-${candidate.id}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        {candidate.photo_url ? (
                          <img
                            src={getImageUrl(candidate.photo_url)}
                            alt={`${candidate.first_name} ${candidate.last_name}`}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
                            <User size={20} className="text-stone-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-stone-900 text-sm">
                            {candidate.first_name} {candidate.last_name}
                          </p>
                          <p className="text-xs text-stone-500">
                            {candidate.gallery_count} artwork{candidate.gallery_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      
                      {/* Mini Gallery Preview */}
                      {candidate.gallery_preview && candidate.gallery_preview.length > 0 && (
                        <div className="flex gap-1">
                          {candidate.gallery_preview.map((img, idx) => (
                            <div key={img.id || idx} className="w-10 h-10 rounded overflow-hidden bg-stone-100">
                              <img
                                src={getImageUrl(img.url)}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedMember?.id === candidate.id && (
                        <div className="mt-2 flex items-center gap-1 text-cafa-red text-xs font-medium">
                          <Check size={14} />
                          Selected
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-stone-50 rounded-lg border border-dashed border-stone-300">
                  <Image size={32} className="mx-auto mb-2 text-stone-300" />
                  <p className="text-stone-500">No members with artwork found</p>
                  <p className="text-sm text-stone-400">Members need to upload artwork to their gallery first</p>
                </div>
              )}
            </div>

            {/* Custom Title & Bio */}
            {selectedMember && (
              <div className="space-y-4 pt-4 border-t border-stone-200">
                <div>
                  <Label htmlFor="customTitle">Custom Title (Optional)</Label>
                  <Input
                    id="customTitle"
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    placeholder="e.g., January 2026 Artist of the Month"
                    className="mt-1"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Leave blank to use default: Artist Member of the Month
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="customBio">Featured Bio (Optional)</Label>
                  <Textarea
                    id="customBio"
                    value={customBio}
                    onChange={(e) => setCustomBio(e.target.value)}
                    placeholder="Write a short bio or statement about the artist..."
                    rows={3}
                    className="mt-1"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    This will display on the homepage instead of their profile bio
                  </p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSelectArtist}
                disabled={!selectedMember || saving}
                className="bg-cafa-red hover:bg-cafa-red-900 text-white"
              >
                {saving ? 'Saving...' : 'Set as Artist of the Month'}
              </Button>
              
              {selectedMember && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedMember(null);
                    setCustomTitle('');
                    setCustomBio('');
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminArtistOfMonth;
